import React, { useMemo } from 'react';
import DatePicker from 'js/components/elements/datepicker/DatePicker';
import {
  DarkMenu,
  DarkMenuOptions,
} from 'js/components/data_flows/cards/FlowRunLogs/components/DarkMenu/DarkMenu';
import {
  RunOption,
  RunSelector,
} from 'js/components/data_flows/cards/FlowRunLogs/components/RunSelector/RunSelector';
import { createLogDateRanges } from '../../utils/logDateRanges';
import { DateRange } from '../../types';
import { SortOrder } from '../../constants';
import {
  getSortOrderLabel,
  getSortOrderOptions,
  getHandledSelectedLevelLabel,
  calculateMinDate,
  expandToFullDayBoundaries,
} from '../../utils/logsNavigationPanel.utils';

import s from './LogsNavigationPanel.module.scss';
import { defineds } from 'js/components/elements/datepicker/defaultRanges';

type Props = {
  omitRunSelector?: boolean;
  omitLogScroller?: boolean;
  omitSortOrderSelector?: boolean;
  value: number; // Kept for backward compatibility
  dateRange?: DateRange; // New prop for date range
  selectedLevel: string;
  logScrollerOptions: DarkMenuOptions;
  levelOptions: DarkMenuOptions;
  runsOptions: { value: string; label: string; onClick: () => void }[];
  selectedRunOption: RunOption | null;
  exploreFlowLogsMode?: boolean;
  expandSingleDaySelection?: boolean;
  sortOrder: SortOrder;
  onSortOrderChange: (sortOrder: SortOrder) => void;
  selectedLogScrollerValue?: SortOrder;
  logScrollerLabel?: string;
  getLogScrollerOptionValue?: (
    option: DarkMenuOptions[0],
    index: number,
  ) => string | number | undefined;
  // actions
  onStartDateChanged: (date: number | DateRange) => void;
  clearSelectedRunOption: () => void;
  createdAt?: string | null;
};

export const LogsNavigationPanel = ({
  omitRunSelector = false,
  omitLogScroller = false,
  omitSortOrderSelector = false,
  value,
  dateRange,
  levelOptions = [],
  selectedLevel,
  logScrollerOptions = [],
  runsOptions = [],
  selectedRunOption = null,
  onStartDateChanged,
  clearSelectedRunOption = () => {},
  exploreFlowLogsMode = false,
  expandSingleDaySelection = false,
  sortOrder,
  onSortOrderChange,
  selectedLogScrollerValue,
  logScrollerLabel = 'Log Scroller',
  getLogScrollerOptionValue,
  createdAt = null,
}: Props) => {
  const sortOrderLabel = getSortOrderLabel(sortOrder);
  const sortOrderOptions = getSortOrderOptions(onSortOrderChange);
  const handledSelectedLevelLabel = getHandledSelectedLevelLabel(selectedLevel);
  const minDate = useMemo(() => calculateMinDate(createdAt), [createdAt]);
  const staticRanges = createLogDateRanges();

  const datePickerData = useMemo(() => {
    let fromTimestamp: number;
    let toTimestamp: number;

    if (dateRange) {
      // Use dateRange if provided
      fromTimestamp = dateRange.from;
      toTimestamp = dateRange.to;
    } else {
      // Fallback to value for backward compatibility
      if (exploreFlowLogsMode && value === 0) {
        fromTimestamp = defineds.endOfToday.getTime();
      } else {
        fromTimestamp = value;
      }
      toTimestamp = defineds.endOfToday.getTime();
    }

    return {
      startDate: new Date(fromTimestamp),
      endDate: new Date(toTimestamp),
      key: 'selection',
    };
  }, [dateRange, value, exploreFlowLogsMode]);

  /**
   * When enabled, equal boundaries represent a full-day calendar selection
   * and get expanded to that day's start/end. A manual multi-day selection
   * (distinct start/end, both landing on local midnight) has its end boundary
   * expanded too, to that last day's end — otherwise the last selected day is
   * silently dropped from the query. Precise ranges ('Last 5 minutes', 'Last 1
   * hour', ...) are untouched because their timestamps aren't day boundaries.
   */
  const handleDateSelect = useMemo(
    () => (selectedRange: { startDate: Date; endDate: Date }) => {
      const dateRangeToPass: DateRange = expandSingleDaySelection
        ? expandToFullDayBoundaries(selectedRange.startDate, selectedRange.endDate)
        : { from: selectedRange.startDate.getTime(), to: selectedRange.endDate.getTime() };

      onStartDateChanged(dateRangeToPass);
    },
    [expandSingleDaySelection, onStartDateChanged],
  );

  return (
    <div className={s.container}>
      <div className={s.leftView}>
        <div className={s.dateRangeSection}>
          <span className={s.label}>Date Range</span>
          <DatePicker
            onSelect={handleDateSelect}
            data={datePickerData}
            staticRanges={staticRanges}
            popoverClassName="logs-navigation-panel-popover"
            extraProps={{
              minDate: minDate,
              maxDate: new Date(),
              showTimePicker: true,
            }}
          />
        </div>

        {!omitRunSelector && (
          <RunSelector
            key="run-selector"
            clearSelectedRunOption={clearSelectedRunOption}
            selectedRunOption={selectedRunOption}
            runsOptions={runsOptions}
          />
        )}

        <DarkMenu
          label="Severity"
          title={handledSelectedLevelLabel}
          options={levelOptions}
          disableAutoCloseAfterClick={true}
        />
      </div>

      <div className={s.rightView}>
        {!omitSortOrderSelector && (
          <DarkMenu
            label="Sort"
            title={sortOrderLabel}
            options={sortOrderOptions}
            description="Controls the display order of log lines in the UI."
          />
        )}

        {!omitLogScroller && (
          <DarkMenu
            title={logScrollerLabel}
            options={logScrollerOptions}
            selectedValue={selectedLogScrollerValue}
            getOptionValue={getLogScrollerOptionValue}
            description="Controls which part of the time window to fetch from the API. Use Sort to change the display order of logs."
          />
        )}
      </div>
    </div>
  );
};
